/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2022 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/
import {
  ReactNode,
  UIEventHandler,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import * as React from "react";
import styled from "styled-components";
import { Heading22 } from "components/shared/Heading";
import useOnClickOutside from "hooks/useOnClickOutside";
import { useNonTextEventListener } from "hooks/useEventListener";
import { CloseButton } from "components/EnhancedSpeech/Buttons";

interface ModalProps {
  title?: string;
  className?: string;
  onClose: VoidFunction;
  closeAltText: string;
  leftItem?: ReactNode;
  topItem?: ReactNode;
  rightItem?: ReactNode;
}

const PanelHeader = styled.div`
  display: flex;
  padding: 24px;
  flex-direction: column;
`;

const PanelHeaderTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const PanelHeaderBottom = styled.div`
  display: flex;
  padding-top: 24px;
  flex: 1;
`;

const Title = styled(Heading22)`
  text-align: start;
  margin-bottom: 0;
  flex-grow: 2;
`;

interface PanelContentProps {
  showShadow: boolean;
}

const ScrollablePanelContent = styled.div<PanelContentProps>`
  flex: 1;
  padding: 2px 24px 24px 24px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  box-shadow: ${(props): string =>
    props.showShadow ? "inset 0 3px 6px rgba(0, 0, 0, 0.08)" : "none"};
`;

export const Panel = styled.div`
  z-index: 10;
  background-color: #ffffff;
  box-shadow: var(--phonos-box-shadow);
  border-radius: 24px;
  box-sizing: border-box;
  border: 0px;

  &:not(:has(${ScrollablePanelContent})) > ${PanelHeader} {
    flex: 1; /* Allow header content to expand when scrollable content is absent */
  }
`;

export const FloatingPanel: React.FC<{
  onClose: VoidFunction;
  className?: string;
}> = ({ onClose, className, children }) => {
  const panelRef = useRef(null);
  useOnClickOutside(panelRef, onClose);
  useNonTextEventListener("keydown", (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      onClose();
    }
  });

  return (
    <Panel
      className={className}
      ref={panelRef}
      role="dialog"
      data-testid="floating-panel"
    >
      {children}
    </Panel>
  );
};

export interface TitledFloatingPanelHandle {
  scrollTop: number;
}

export const TitledFloatingPanel = React.forwardRef<
  TitledFloatingPanelHandle,
  React.PropsWithChildren<ModalProps>
>(
  (
    {
      title,
      className,
      children,
      onClose,
      closeAltText,
      leftItem,
      topItem,
      rightItem,
    },
    ref
  ) => {
    const scrollableContentRef = useRef<HTMLDivElement>(null);
    const [showShadow, setShowShadow] = useState(false);

    const scrollListener: UIEventHandler<HTMLDivElement> = (event) => {
      const target = event.target as Element;
      setShowShadow(target.scrollTop > 0);
    };

    useImperativeHandle(
      ref,
      () => ({
        get scrollTop(): number {
          return scrollableContentRef.current?.scrollTop ?? 0;
        },
        set scrollTop(value: number) {
          if (scrollableContentRef.current) {
            requestAnimationFrame(() => {
              if (scrollableContentRef.current) {
                scrollableContentRef.current.scrollTop = value;
              }
            });
          }
        },
      }),
      []
    );

    return (
      <ScrollablePanel onClose={onClose} className={className}>
        <PanelHeader data-testid="floating-panel-header">
          <PanelHeaderTop>
            {leftItem}
            <Title data-testid="floating-panel-header-title">{title}</Title>
            {rightItem}
            <CloseButton onClick={onClose} title={closeAltText} />
          </PanelHeaderTop>
          {topItem ? <PanelHeaderBottom>{topItem}</PanelHeaderBottom> : ""}
        </PanelHeader>
        {children && (
          <ScrollablePanelContent
            ref={scrollableContentRef}
            showShadow={showShadow}
            onScroll={scrollListener}
            data-testid="scrollable-panel-content"
          >
            {children}
          </ScrollablePanelContent>
        )}
      </ScrollablePanel>
    );
  }
);

const ScrollablePanel = styled(FloatingPanel)`
  display: flex;
  flex-direction: column;
`;
